<script setup>
import { ref, computed } from 'vue'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const sliderRef = ref(null)
const props = defineProps({
  cameraList: {
    type: Array,
    default: () => []
  }
})
const sliderValue = defineModel('sliderValue', { type: Number })
const maxIndex = computed(() => props.cameraList.length - 1)
const { isPlaying, onClickPlay, onClickStop } = useAnimate(sliderValue, maxIndex)
const selectItem = (index) => {
  if (index >= 0 && index < props.cameraList.length) {
    sliderValue.value = index
  }
}
const translateTime = (unixTimestamp) => {
  return dayjs.unix(unixTimestamp).tz('Asia/Tokyo').format('HH:mm')
}
const translateDateTime = (unixTimestamp) => {
  return dayjs.unix(unixTimestamp).tz('Asia/Tokyo').format('DD日 (ddd) HH:mm')
}
const updateSliderBackground = () => {
  if (sliderRef.value) {
    const value = (sliderValue.value / sliderRef.value.max) * 100
    sliderRef.value.style.background = `linear-gradient(to right, #3569c0 0%, #3569c0 ${value}%, #8793ae ${value}%, #8793ae 100%)`
  }
}
const onSliderInput = () => {
  selectItem(sliderValue.value)
}
watch(() => sliderValue.value, () => {
  updateSliderBackground()
}, { immediate: true })
</script>

<template>
  <div
    class="slider__inner"
    :style="{ '--time-list-length': props.cameraList.length }"
  >
    <div class="controller">
      <p>
        {{ translateDateTime(props.cameraList[sliderValue]?.date) }}
      </p>
    </div>
    <div class="content">
      <button
        v-show="!isPlaying"
        data-button="play"
        @click="onClickPlay()"
      />
      <button
        v-show="isPlaying"
        data-button="stop"
        @click="onClickStop()"
      />
      <div class="range">
        <input
          ref="sliderRef"
          v-model.number="sliderValue"
          type="range"
          min="0"
          :max="maxIndex"
          step="1"
          @input="onSliderInput()"
        >
        <div
          class="list"
        >
          <p
            v-for="(item, index) in cameraList"
            :key="index"
            class="scale"
          >
            <span v-if="index===0 || index === maxIndex">
              {{ translateTime(item.date) }}
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.slider__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 5px 10px;
  background-color: #eef1f8;
}
.controller {
  display: flex;
  align-items: center;
  gap: 5px;
  user-select: none;
  p {
    min-width: 150px;
    padding: 6px 15px 5px;
    background-color: #fff;
    border-radius: 100vmax;
    color: #303030;
    font-size: 12px;
    line-height: 12px;
    text-align: center;
  }
}
.content {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}
button {
  width: 25px;
  height: 25px;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 2px 0 rgb(0 0 0 / 0.15);
  background-position: center;
  background-repeat: no-repeat;
  background-size: 12px;
  cursor: pointer;
  &[data-button="prev"] {
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_prev.svg");
  }
  &[data-button="next"] {
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_next.svg");
  }
  &[data-button="play"] {
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_play.svg");
  }
  &[data-button="stop"] {
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_pause.svg");
  }
  &:disabled {
    opacity: 0.4;
    pointer-events: none;
  }
  @media (any-hover: hover) {
    transition: opacity .2s ease-in-out;
    &:hover {
      opacity: .6;
    }
  }
}
.range {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  input {
    position: relative;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
    outline: none;
    width: calc((100% - 20px) / var(--time-list-length) * (var(--time-list-length) - 1) + 12px);
    height: 3px;
    margin-inline: auto;
    background-color: #8793ae;
    border-radius: 100vmax;
    border: none;
    z-index: 2;
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      background-color: #3569c0;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      box-shadow: none;
    }
    &::-moz-range-thumb {
      background: #3569c0;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      box-shadow: none;
      border: none;
    }
  }
  .list {
    display: flex;
    user-select: none;
    padding: 0 10px;
  }
  .scale {
    position: relative;
    flex: 1;
    padding-top: 6px;
    text-align: center;
    font-size: 0;
    color: transparent;
    font-weight: 600;
    line-height: 12px;
    width: calc((100% - 20px) / var(--time-list-length));
    white-space: nowrap;
    display: flex;
    justify-content: center;
    &:first-child,
    &:last-child {
      font-size: 12px;
      color: #303030;
      &::before {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 1px;
        height: 6px;
        background-color: #8793ae;
        content: '';
      }
    }
  }
}
@media (min-width: 830px) {
  .slider__inner {
    gap: 12px;
    padding: 10px;
  }
  .controller {
    p {
      font-size: 14px;
      line-height: 14px;
    }
  }
  .range {
    input {
      width: calc((100% - 20px) / var(--time-list-length) * (var(--time-list-length) - 1) + 16px);
      &::-webkit-slider-thumb {
      width: 16px;
      height: 16px;
      }
      &::-moz-range-thumb {
        width: 16px;
        height: 16px;
      }
    }
    .scale {
      padding-top: 12px;
      &::before {
        height: 10px;
      }
    }
  }
}
</style>
